import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { useMemo } from 'react'
import { pathOr, includes, isEmpty } from 'ramda'
import { ValueText } from '@/features/Application/elements'
import { InformationTable, TableTitle } from '@/features/Application/elements/tables'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { EquifaxLogomark } from '@/components/icons'
import MobileBankruptcies from './mobile'
import Empty from './empty'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 18px;
`

const Bankruptcies = ({ data = {}, application = {}, isLocked, isLoading, standalone }) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)

  const isActive =
    application?.trust_score?.status === 'active' ||
    (standalone && application?.status !== 'failed')
  const { details = [], rate, warnings } = data ?? {}
  const noBankruptcies = isEmpty(details)
  const showEmpty = !isActive || noBankruptcies

  const status = {
    poor: t('l.application.view.poor.label', 'Poor'),
    average: t('l.application.view.average.label', 'Average'),
    very_good: t('l.application.view.very_good.label', 'Very Good'),
    excellent: t('l.application.view.excellent.label', 'Excellent')
  }

  const statusText = isActive ? status[rate] : status['poor']
  const statusRate = isActive ? rate : 'poor'
  const title = t('l.application.view.bankruptcies.label', 'Bankruptcies')

  const columns = useMemo(
    () => [
      {
        id: 'header row',
        Header: t('l.application.view.records.label', 'Records'),
        Cell: ({ row }) => {
          const { index } = row

          const count = index + 1
          return (
            <ValueText type="small" isLocked={isLocked}>
              <Trans t={t} i18nKey="l.application.view.records_count.label" count={count}>
                Record {{ count }}
              </Trans>
            </ValueText>
          )
        }
      },
      {
        Header: t('l.application.view.liability.label', 'Liability'),
        Cell: ({ row: { original } }) => {
          const value = pathOr('-', ['liability'], original)
          const hasWarning = includes('liability', warnings || [])

          return (
            <ValueText isLocked={isLocked} hasWarning={hasWarning} type="small">
              {value ?? '-'}
            </ValueText>
          )
        }
      },
      {
        Header: t('l.application.view.description.label', 'Description'),
        Cell: ({ row: { original } }) => {
          const value = pathOr('-', ['disposition'], original)

          return (
            <ValueText isLocked={isLocked} type="small">
              {value}
            </ValueText>
          )
        }
      }
    ],
    [isLocked]
  )

  return (
    <Wrapper>
      <TableTitle
        title={title}
        showStatus={true}
        statusType={statusRate}
        statusText={statusText}
        isLocked={isLocked}
        isLoading={isLoading}
        icon={<EquifaxLogomark />}
      />
      {showEmpty ? (
        <Empty isLocked={isLocked} isActive={isActive} isLoading={isLoading} />
      ) : (
        <InformationTable data={details} columns={columns} hideHeader={isMobile} />
      )}
    </Wrapper>
  )
}

export { MobileBankruptcies }
export default Bankruptcies
